import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui';
import { alert } from '../utils/toast';
import Swal from 'sweetalert2';
import type { IntegrationRoute } from '../types';
import { ArrowLeft, ArrowRight, Plus, Trash2, Shuffle } from 'lucide-react';

export const MappingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [requestMappings, setRequestMappings] = useState<Record<string, string>>({});
  const [responseMappings, setResponseMappings] = useState<Record<string, string>>({});
  const [sampleRequest, setSampleRequest] = useState('{}');
  const [sampleResponse, setSampleResponse] = useState('{}');

  const { data: route } = useQuery({
    queryKey: ['integration-route', id],
    queryFn: async () => {
      const response = await api.get<IntegrationRoute>(`/admin/integration-routes/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  React.useEffect(() => {
    if (route) {
      if (route.request_mapping?.field_mappings) {
        setRequestMappings(route.request_mapping.field_mappings);
      }
      if (route.response_mapping?.field_mappings) {
        setResponseMappings(route.response_mapping.field_mappings);
      }
    }
  }, [route]);

  const saveMutation = useMutation({
    mutationFn: async (data: {
      request: Record<string, string>;
      response: Record<string, string>;
    }) => {
      await api.put(`/admin/integration-routes/${id}`, {
        request_mapping: { field_mappings: data.request },
        response_mapping: { field_mappings: data.response },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-route', id] });
      void alert.success('Guardado!', 'Mapeamentos guardados com sucesso!');
    },
  });

  const previewMutation = useMutation({
    mutationFn: async (data: {
      type: 'request' | 'response';
      sample: string;
      mappings: Record<string, string>;
    }) => {
      const response = await api.post('/admin/mappings/preview', {
        type: data.type,
        sample: JSON.parse(data.sample),
        mappings: data.mappings,
      });
      return response.data?.data || response.data;
    },
  });

  const addRequestMapping = async () => {
    const { value: source } = await Swal.fire({
      title: 'Adicionar Mapeamento de Pedido',
      input: 'text',
      inputLabel: 'Campo de Origem (ex: user.name):',
      inputPlaceholder: 'user.name',
      showCancelButton: true,
      confirmButtonText: 'Seguinte',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (!source) return;

    const { value: target } = await Swal.fire({
      title: 'Adicionar Mapeamento de Pedido',
      input: 'text',
      inputLabel: `Mapear "${source}" para Campo de Destino (ex: userName):`,
      inputPlaceholder: 'userName',
      showCancelButton: true,
      confirmButtonText: 'Adicionar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (source && target) {
      setRequestMappings({ ...requestMappings, [source]: target });
    }
  };

  const addResponseMapping = async () => {
    const { value: source } = await Swal.fire({
      title: 'Adicionar Mapeamento de Resposta',
      input: 'text',
      inputLabel: 'Campo de Origem (ex: data.id):',
      inputPlaceholder: 'data.id',
      showCancelButton: true,
      confirmButtonText: 'Seguinte',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (!source) return;

    const { value: target } = await Swal.fire({
      title: 'Adicionar Mapeamento de Resposta',
      input: 'text',
      inputLabel: `Mapear "${source}" para Campo de Destino (ex: userId):`,
      inputPlaceholder: 'userId',
      showCancelButton: true,
      confirmButtonText: 'Adicionar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (source && target) {
      setResponseMappings({ ...responseMappings, [source]: target });
    }
  };

  const removeRequestMapping = (key: string) => {
    const newMappings = { ...requestMappings };
    delete newMappings[key];
    setRequestMappings(newMappings);
  };

  const removeResponseMapping = (key: string) => {
    const newMappings = { ...responseMappings };
    delete newMappings[key];
    setResponseMappings(newMappings);
  };

  const handleSave = () => {
    saveMutation.mutate({ request: requestMappings, response: responseMappings });
  };

  const handlePreviewRequest = () => {
    try {
      JSON.parse(sampleRequest);
      previewMutation.mutate({ type: 'request', sample: sampleRequest, mappings: requestMappings });
    } catch {
      void alert.error('Erro', 'JSON inválido no exemplo de pedido');
    }
  };

  const handlePreviewResponse = () => {
    try {
      JSON.parse(sampleResponse);
      previewMutation.mutate({
        type: 'response',
        sample: sampleResponse,
        mappings: responseMappings,
      });
    } catch {
      void alert.error('Erro', 'JSON inválido no exemplo de resposta');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/integration-routes">Rotas de Integração</BreadcrumbItem>
        <BreadcrumbItem>Editar Mapeamentos</BreadcrumbItem>
      </Breadcrumbs>

      <div>
        <Link
          to="/integration-routes"
          className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm mb-3 font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para as rotas</span>
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
          <Shuffle className="w-8 h-8 text-primary-500" />
          <span>Editor de Mapeamento de Pedidos e Respostas</span>
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
          {route?.name} - Configurar transformações de campos
        </p>
      </div>

      {saveMutation.isError && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
          <p className="text-sm text-rose-800 dark:text-rose-300">
            {getErrorMessage(saveMutation.error)}
          </p>
        </div>
      )}

      {/* Request Mapping */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Mapeamento de Pedidos
          </h2>
          <button
            onClick={addRequestMapping}
            className="btn-base btn-sm btn-secondary flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Mapeamento</span>
          </button>
        </div>

        {Object.keys(requestMappings).length > 0 ? (
          <div className="space-y-2 mb-4">
            {Object.entries(requestMappings).map(([source, target]) => (
              <div
                key={source}
                className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-2 font-mono text-sm">
                  <span className="text-blue-600 dark:text-blue-400">{source}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <span className="text-emerald-600 dark:text-emerald-400">{target}</span>
                </div>
                <button
                  onClick={() => removeRequestMapping(source)}
                  className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 hover:text-rose-700 text-xs font-semibold ml-4"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remover</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
            Nenhum mapeamento de pedido configurado
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-modern">Exemplo de Pedido (JSON)</label>
            <textarea
              className="input-modern font-mono text-xs h-64"
              rows={10}
              value={sampleRequest}
              onChange={(e) => setSampleRequest(e.target.value)}
            />
          </div>
          <div>
            <label className="label-modern">Saída de Pré-visualização</label>
            {previewMutation.isSuccess && previewMutation.variables?.type === 'request' ? (
              <pre className="bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-200 p-3 rounded-xl text-xs overflow-x-auto h-64 border border-slate-200 dark:border-slate-700 font-mono">
                {JSON.stringify(previewMutation.data, null, 2)}
              </pre>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl text-xs text-slate-500 dark:text-slate-400 h-64 flex items-center justify-center border border-slate-200 dark:border-slate-700">
                Clique em "Pré-visualizar" para ver a saída transformada
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handlePreviewRequest}
          disabled={previewMutation.isPending}
          className="btn-base btn-md btn-secondary mt-4"
        >
          {previewMutation.isPending ? 'A processar...' : 'Pré-visualizar Transformação de Pedido'}
        </button>
      </div>

      {/* Response Mapping */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Mapeamento de Respostas
          </h2>
          <button
            onClick={addResponseMapping}
            className="btn-base btn-sm btn-secondary flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Mapeamento</span>
          </button>
        </div>

        {Object.keys(responseMappings).length > 0 ? (
          <div className="space-y-2 mb-4">
            {Object.entries(responseMappings).map(([source, target]) => (
              <div
                key={source}
                className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-2 font-mono text-sm">
                  <span className="text-blue-600 dark:text-blue-400">{source}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <span className="text-emerald-600 dark:text-emerald-400">{target}</span>
                </div>
                <button
                  onClick={() => removeResponseMapping(source)}
                  className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 hover:text-rose-700 text-xs font-semibold ml-4"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Remover</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
            Nenhum mapeamento de resposta configurado
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-modern">Exemplo de Resposta (JSON)</label>
            <textarea
              className="input-modern font-mono text-xs h-64"
              rows={10}
              value={sampleResponse}
              onChange={(e) => setSampleResponse(e.target.value)}
            />
          </div>
          <div>
            <label className="label-modern">Saída de Pré-visualização</label>
            {previewMutation.isSuccess && previewMutation.variables?.type === 'response' ? (
              <pre className="bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-200 p-3 rounded-xl text-xs overflow-x-auto h-64 border border-slate-200 dark:border-slate-700 font-mono">
                {JSON.stringify(previewMutation.data, null, 2)}
              </pre>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl text-xs text-slate-500 dark:text-slate-400 h-64 flex items-center justify-center border border-slate-200 dark:border-slate-700">
                Clique em "Pré-visualizar" para ver a saída transformada
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handlePreviewResponse}
          disabled={previewMutation.isPending}
          className="btn-base btn-md btn-secondary mt-4"
        >
          {previewMutation.isPending
            ? 'A processar...'
            : 'Pré-visualizar Transformação de Resposta'}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <Link to="/integration-routes" className="btn-base btn-md btn-secondary">
          Cancelar
        </Link>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="btn-base btn-md btn-primary"
        >
          {saveMutation.isPending ? 'A guardar...' : 'Guardar Mapeamentos'}
        </button>
      </div>
    </div>
  );
};
